import type { FormEventHandler } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import type { CreateAuditPlanInput } from "@/lib/rpc";
import type { CreateAuditPlanData } from "@/lib/audit-plan";
import type { UseMutationResult } from "@tanstack/react-query";

const futureYears = (count: number = 3): string[] => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: count }, (_, i) => String(currentYear + i + 1));
};

type NewAuditPlanDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mutation: UseMutationResult<
    CreateAuditPlanData,
    Error,
    CreateAuditPlanInput
  >;
};

export const NewAuditPlanDialog = ({
  open,
  onOpenChange,
  mutation,
}: NewAuditPlanDialogProps) => {
  const currentYear = "2025";

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const data = new FormData(event.target as HTMLFormElement);
    mutation.mutate(Object.fromEntries(data) as unknown as CreateAuditPlanInput);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create New Audit Plan</DialogTitle>
          <DialogDescription>
            Define a new audit plan for a specific year.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Plan Name
              </Label>
              <Input
                name="title"
                className="col-span-3"
                placeholder={`e.g., Internal Audit Plan ${currentYear}`}
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">
                Description
              </Label>
              <Input
                name="description"
                className="col-span-3"
                placeholder="Brief description of the audit plan..."
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="year" className="text-right">
                Year
              </Label>
              <Select name="referenceYear">
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  {futureYears().map((year) => (
                    <SelectItem key={year} value={year}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit">Create Plan</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
